import { Box, colors, Panel, Search, spacing, Tabs, Typography } from "@procore/core-react";
import { useTranslation } from "react-i18next";
import { Rnd } from "react-rnd";

import { HEADER_BORDER, WINDOW_HEADER_ROW_CLASS } from "./constants";
import { useDrawerPeekTarget } from "./drawerPeekTargetContext";
import { EmptyState } from "./emptyState";
import { LoadingState } from "./loadingState";
import type { WindowChromeProps, WindowProps } from "./types";
import { useWindowGeometry } from "./useWindowGeometry";
import { useWindowPlatformMode } from "./useWindowPlatformMode";
import { WindowChromeControls } from "./windowChromeControls";
import { useWindowDocked } from "./windowDockedContext";
import { useWindowMinimize, type WindowMinimize } from "./windowMinimizeContext";
import { useWindowPositionOverride } from "./windowPositionContext";
import { useWindowSizeOverride } from "./windowSizeContext";

export type {
    EmptyStateConfig,
    WindowBulkActionsProps,
    WindowChromeProps,
    WindowProps,
    WindowSearchProps,
    WindowTabItem,
    WindowTabsProps,
} from "./types";

export { VIEWER_INSET } from "./constants";
export { WindowChromeControls } from "./windowChromeControls";
export type { WindowChromeControlsProps } from "./windowChromeControls";

// The header never squashes: in `contentOnly` mode it is a flex child alongside the
// scrollable body, and the mobile drawer sizes its collapsed peek from this height.
const HEADER_STYLE = { flexShrink: 0 } as const;

const CHROME_ROW_STYLE = {
    paddingLeft: spacing.lg,
    paddingRight: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottom: HEADER_BORDER,
} as const;

// Internal — adds the resolved minimize control. `WindowChromeProps` (public)
// omits it because consumers use `minimized`/`onMinimizedChange` instead.
type WindowChromeInternalProps = WindowChromeProps & {
    minimize?: WindowMinimize;
};

function WindowChrome({
    title,
    subheader,
    titleAdornment,
    onClose,
    tabs,
    search,
    bulkActions,
    minimize,
}: WindowChromeInternalProps) {
    // Sizes the mobile drawer's collapsed peek to the title row. No-op in every other mode.
    const registerPeekTarget = useDrawerPeekTarget();
    const isMinimized = minimize?.minimized ?? false;
    // `onClose` and `minimize` are auto-supplied by WindowManager for every
    // registered window, so neither can be a sole trigger — would force chrome
    // onto SDK windows that bring their own header (the double-header bug).
    const hasTitleRow = Boolean(title || subheader || titleAdornment);
    const hasRowsBelowTitle = !isMinimized && Boolean(tabs || search || bulkActions);
    const showTitleBorder = !isMinimized && !hasRowsBelowTitle;

    if (!hasTitleRow && !hasRowsBelowTitle) {
        return null;
    }

    return (
        <div className="window-header" style={HEADER_STYLE}>
            {hasTitleRow && (
                <Box
                    ref={registerPeekTarget}
                    className={WINDOW_HEADER_ROW_CLASS}
                    padding="lg"
                    style={{
                        cursor: "move",
                        userSelect: "none",
                        borderBottom: showTitleBorder ? HEADER_BORDER : undefined,
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box display="flex" flexDirection="column" style={{ flex: 1, minWidth: 0 }}>
                            <Box display="flex" alignItems="center" style={{ gap: spacing.sm, flexWrap: "wrap" }}>
                                {title && (
                                    <Typography intent="h3" weight="semibold">
                                        {title}
                                    </Typography>
                                )}
                                {titleAdornment}
                            </Box>
                            {!isMinimized && subheader && (
                                <Box style={{ marginTop: spacing.xs, overflowWrap: "anywhere" }}>
                                    <Typography intent="small" weight="semibold" color="gray45">
                                        {subheader}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <WindowChromeControls onClose={onClose} minimize={minimize ?? null} />
                    </Box>
                </Box>
            )}
            {!isMinimized && tabs && (
                <Box style={{ ...CHROME_ROW_STYLE, paddingTop: 0, paddingBottom: 0 }}>
                    <Tabs data-testid={tabs["data-testid"]}>
                        {tabs.items.map((tab) => (
                            <Tabs.Tab
                                key={tab.id}
                                selected={tabs.activeId === tab.id}
                                onClick={() => tabs.onChange(tab.id)}
                                data-testid={tab["data-testid"]}
                            >
                                {tab.label}
                            </Tabs.Tab>
                        ))}
                    </Tabs>
                </Box>
            )}
            {!isMinimized && search && (
                <Box style={CHROME_ROW_STYLE}>
                    <Search
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        placeholder={search.placeholder}
                        autoComplete="off"
                        onSubmit={search.onSubmit}
                        data-testid={search["data-testid"]}
                    />
                </Box>
            )}
            {!isMinimized && bulkActions && (
                <Box
                    style={{
                        ...CHROME_ROW_STYLE,
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.sm,
                        backgroundColor: bulkActions.active ? colors.blue96 : colors.white,
                        transition: "background-color 0.2s ease",
                    }}
                >
                    {bulkActions.children}
                </Box>
            )}
        </div>
    );
}

function renderHeader(props: WindowProps, minimize?: WindowMinimize) {
    if (props.header != null) {
        return (
            <div className="window-header" style={HEADER_STYLE}>
                {props.header}
            </div>
        );
    }
    return (
        <WindowChrome
            title={props.title}
            subheader={props.subheader}
            titleAdornment={props.titleAdornment}
            onClose={props.onClose}
            tabs={props.tabs}
            search={props.search}
            bulkActions={props.bulkActions}
            minimize={minimize}
        />
    );
}

// Explicit `minimized`/`onMinimizedChange` props beat the `WindowMinimizeContext`
// supplied by `WindowManager` for managed windows.
function useEffectiveMinimize(props: WindowProps): WindowMinimize | undefined {
    const ctx = useWindowMinimize();
    if (props.onMinimizedChange) {
        const minimized = props.minimized ?? false;
        const onMinimizedChange = props.onMinimizedChange;
        return { minimized, onToggle: () => onMinimizedChange(!minimized), isDocked: false };
    }
    return ctx ?? undefined;
}

export function Window(props: WindowProps) {
    // Docked windows keep the Rnd shell — WindowFull geometry-locks them to the
    // dock slot. Only mobile/contentOnly skips it.
    const { isContentOnly } = useWindowPlatformMode();
    const minimize = useEffectiveMinimize(props);

    if (isContentOnly) {
        return (
            <div
                style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                }}
            >
                {renderHeader(props, minimize)}
                <div
                    style={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflow: "auto",
                    }}
                >
                    <WindowOverlays
                        error={props.error}
                        loading={props.loading}
                        loadingMessage={props.loadingMessage}
                        emptyState={props.emptyState}
                    >
                        {props.children}
                    </WindowOverlays>
                </div>
            </div>
        );
    }
    return <WindowFull {...props} minimize={minimize} />;
}

function useEffectiveSize(
    propSize: WindowProps["size"],
    propMinSize: WindowProps["minSize"],
): { size: WindowProps["size"]; minSize: WindowProps["minSize"] } {
    // `WindowSizeContext` override (e.g. dock drag-out) wins over both `size`
    // and `minSize` — otherwise `useWindowGeometry`'s `max(size, minSize)`
    // floor would snap the panel back up to the registered minSize.
    const override = useWindowSizeOverride();
    if (override) {
        return { size: override, minSize: override };
    }
    return { size: propSize, minSize: propMinSize };
}

interface OverlayProps {
    error?: string | null;
    loading?: boolean;
    loadingMessage?: string;
    emptyState?: WindowProps["emptyState"];
    children: React.ReactNode;
}

// Priority: error > loading > emptyState. Children are kept mounted but
// hidden when any overlay is active so internal state survives state flips.
function WindowOverlays({ error, loading, loadingMessage, emptyState, children }: OverlayProps) {
    const { t } = useTranslation();
    const hasOverlay = Boolean(error || loading || emptyState);

    const overlayStyle: React.CSSProperties = {
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    return (
        <div style={{ position: "relative", height: "100%" }}>
            <div style={{ display: hasOverlay ? "none" : "block", height: "100%" }}>{children}</div>

            {error ? (
                <div style={overlayStyle}>
                    <EmptyState title={t("error")} message={error} data-testid="error-state" />
                </div>
            ) : loading ? (
                <div style={overlayStyle}>
                    <LoadingState message={loadingMessage || t("loading")} />
                </div>
            ) : emptyState ? (
                <div style={overlayStyle}>
                    <EmptyState
                        title={emptyState.title}
                        message={emptyState.message}
                        showIcon={emptyState.showIcon}
                        data-testid={emptyState["data-testid"]}
                    />
                </div>
            ) : null}
        </div>
    );
}

function WindowFull(props: WindowProps & { minimize?: WindowMinimize }) {
    const {
        children,
        onFocus,
        position,
        size: propSize = { width: 352, height: 645 },
        minSize: propMinSize = { width: 352, height: 625 },
        loading = false,
        loadingMessage,
        error = null,
        emptyState,
        secondaryBgColor = false,
        autoHeight = false,
        minimize,
        "data-testid": dataTestId,
    } = props;
    const { size, minSize } = useEffectiveSize(propSize, propMinSize);
    // Slot-level override (FloatingWindowSlot) wins over the prop so SDK
    // consumers that forget to forward `position` still follow store state.
    const positionOverride = useWindowPositionOverride();
    const effectivePosition = positionOverride ?? position;
    const isMinimized = minimize?.minimized ?? false;
    const isDocked = useWindowDocked();
    const header = renderHeader(props, minimize);
    const hasWindowManagedHeader =
        props.header != null || Boolean(props.title || props.subheader || props.titleAdornment);
    const shouldHideBodyWhenMinimized = hasWindowManagedHeader;
    // Minimized windows collapse to their natural header height. Expanded docked
    // windows stay geometry-locked to the flex slot, but a minimized docked window
    // must shrink to its header so WindowSlot can measure that height and reserve a
    // matching slot — otherwise SDK windows (whose header heights vary) overflow
    // into the gap below and lose their inter-window margin.
    const heightIsAuto = isMinimized || (autoHeight && !isDocked);

    const geometry = useWindowGeometry({
        position: effectivePosition,
        size,
        minSize,
        autoHeight: heightIsAuto,
        locked: isDocked,
    });

    const handleDragStart = () => {
        geometry.onDragStart();
        onFocus?.();
    };

    return (
        <Rnd
            ref={geometry.setRndRef}
            position={geometry.position}
            size={geometry.size}
            minWidth={geometry.minWidth}
            minHeight={geometry.minHeight}
            disableDragging={isDocked}
            enableResizing={!isMinimized && !isDocked}
            bounds="parent"
            dragHandleClassName="window-header"
            cancel="input, textarea, select, button, [contenteditable]"
            style={{
                pointerEvents: "auto",
            }}
            onDragStart={handleDragStart}
            onDragStop={geometry.onDragStop}
            onResizeStop={geometry.onResizeStop}
        >
            <Panel
                style={{
                    boxShadow: "2px 0px 6px 0px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                    ...(heightIsAuto ? { maxHeight: "80vh", display: "flex", flexDirection: "column" } : {}),
                }}
                data-testid={dataTestId}
            >
                {header}
                <Panel.Body style={isMinimized && shouldHideBodyWhenMinimized ? { display: "none" } : undefined}>
                    <Box
                        style={{
                            overflow: "auto",
                            width: "100%",
                            height: autoHeight ? undefined : "100%",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: secondaryBgColor ? colors.gray96 : undefined,
                        }}
                    >
                        <WindowOverlays
                            error={error}
                            loading={loading}
                            loadingMessage={loadingMessage}
                            emptyState={emptyState}
                        >
                            {children}
                        </WindowOverlays>
                    </Box>
                </Panel.Body>
            </Panel>
        </Rnd>
    );
}
